import { ActionAfterViewed } from '@cian/action-after-viewed-component';
import * as React from 'react';
import { useSelector } from 'react-redux';

import { LoadableNewbuildingOffersCarousel as NewbuildingOffersCarousel } from './LoadableNewbuildingOffersCarousel';
import { NewbuildingOffersCarouselLayout } from './components/NewbuildingOffersCarouselLayout';
import {
  ACTION_AFTER_VIEWED_TIMEOUT,
  BANNER_POSITION,
  NEWBUILDING_OFFERS_CAROUSEL_OPTIONS,
} from 'shared/constants/newbuildingCarouselOffers';
import { NewbuildingOfferCard } from 'shared/containers/NewbuildingOffersCarouselContainer/components/NewbuildingOfferCard';
import {
  trackNewbuildingClick,
  trackOfferClick,
  trackCarouselChange,
  trackOffersCarouselShow,
} from 'shared/containers/NewbuildingOffersCarouselContainer/tracking';
import { getJsonQuery } from 'shared/selectors/getJsonQuery';
import { NewbuildingBrokerConsultantCarouselBannerContainer } from 'shared/serp/components/NewbuildingBrokerConsultantBanner';
import { resolveGetOffersForSpecificNewbuildingSearchSlider } from 'shared/services/resolveGetOffersForSpecificNewbuildingSearchSlider';
import { INewbuildingOffer } from 'shared/types/newbuildingOffersCarousel';
import { useApplicationContext } from 'shared/utils/applicationContext';

type TCarouselItem = INewbuildingOffer | { type: 'banner' };

interface INewbuildingOffersCarouselContainerProps {
  showBanner?: boolean;
  withMargin?: boolean;
  onError?: () => void;
  onNoData?: () => void;
}

export const NewbuildingOffersCarouselContainer: React.FC<INewbuildingOffersCarouselContainerProps> = ({
  showBanner,
  withMargin,
  onError,
  onNoData,
}) => {
  const { logger, httpApi } = useApplicationContext();
  const jsonQuery = useSelector(getJsonQuery);

  const [isShowSkeleton, setIsShowSkeleton] = React.useState(true);
  const [rawOffers, setRawOffers] = React.useState<INewbuildingOffer[]>([]);
  const [shownSlides, setShownSlides] = React.useState<number[]>([]);

  const handleSlideChange = React.useCallback(
    (activeSlideIndex: number) => {
      const newSlides = rawOffers
        .map(offer => offer.id)
        .slice(activeSlideIndex, activeSlideIndex + NEWBUILDING_OFFERS_CAROUSEL_OPTIONS.slidesPerView);

      const isPossibleSendEvent = !!newSlides.filter(newSlide => !shownSlides.includes(newSlide)).length;

      if (!isPossibleSendEvent) {
        return;
      }

      setShownSlides(oldSlides => [...new Set([...oldSlides, ...newSlides])]);

      trackCarouselChange({
        activeSlideIndex,
        offers: rawOffers,
        isKpn: Boolean(showBanner),
      });
    },
    [rawOffers, shownSlides, showBanner],
  );

  React.useEffect(() => {
    if (!jsonQuery) {
      setRawOffers([]);
      setIsShowSkeleton(false);

      return;
    }

    let isMounted = true;

    const fetchSlides = async () => {
      setIsShowSkeleton(true);
      const offers = await resolveGetOffersForSpecificNewbuildingSearchSlider({
        httpApi,
        logger,
        parameters: { mainSearchJsonQuery: jsonQuery },
      });

      if (!isMounted) {
        return;
      }

      if (!offers) {
        onError?.();
        setRawOffers([]);
      } else if (offers.length === 0) {
        onNoData?.();
        setRawOffers([]);
      } else {
        setRawOffers(offers);
      }

      setIsShowSkeleton(false);
    };

    fetchSlides();

    return () => {
      isMounted = false;
    };
  }, [httpApi, logger, jsonQuery, onError, onNoData]);

  const offerSlides = React.useMemo(() => {
    const slides: TCarouselItem[] = [...rawOffers];

    if (showBanner && BANNER_POSITION >= 0 && BANNER_POSITION <= slides.length) {
      slides.splice(BANNER_POSITION, 0, { type: 'banner' });
    }

    return slides.map((item, index) => {
      if ('type' in item && item.type === 'banner') {
        return (
          <NewbuildingBrokerConsultantCarouselBannerContainer
            onClick={() => trackOfferClick({ position: index })}
            key="banner"
          />
        );
      }

      const offer = item as INewbuildingOffer;

      return (
        <NewbuildingOfferCard
          onNewbuilldingClick={() =>
            trackNewbuildingClick({
              offerId: offer.id,
              newbuildingId: offer.newbuilding.id,
              position: index,
            })
          }
          onOfferClick={() =>
            trackOfferClick({
              offerId: offer.id,
              newbuildingId: offer.newbuilding.id,
              position: index,
            })
          }
          key={offer.id}
          {...offer}
        />
      );
    });
  }, [rawOffers, showBanner]);

  const handleAfterInit = React.useCallback(() => {
    if (!rawOffers.length) {
      return;
    }

    trackOffersCarouselShow({
      offers: rawOffers,
      isKpn: Boolean(showBanner),
    });
  }, [rawOffers, showBanner]);

  if (!isShowSkeleton && rawOffers.length === 0) {
    return null;
  }

  return (
    <NewbuildingOffersCarouselLayout
      showSkeleton={isShowSkeleton}
      showBanner={showBanner}
      withMargin={withMargin}
      carousel={
        <ActionAfterViewed
          percentVisible={0.5}
          callback={handleAfterInit}
          timeoutCallback={ACTION_AFTER_VIEWED_TIMEOUT}
        >
          <NewbuildingOffersCarousel
            onSlideChange={swiper => handleSlideChange(swiper.activeIndex)}
            {...NEWBUILDING_OFFERS_CAROUSEL_OPTIONS}
          >
            {offerSlides}
          </NewbuildingOffersCarousel>
        </ActionAfterViewed>
      }
    />
  );
};
